// ===================================================================
//  Diagram hotspot label placement — OVERLAY-LABEL-PLACEMENT-1 (2026-10-02)
//  + OVERLAY-LABEL-PLACEMENT-2 (2026-10-02): `leader` valid on any side.
//  + OVERLAY-LABEL-HIDDEN-ONLY-1 (2026-10-04): on a page with a valid
//    `label_pill`, a hotspot WITHOUT `label_anchor` gets no label
//    (`planHotspotLabel`).
//
//  The bot MAY serve, per hotspot, a `label_anchor` (where to draw the
//  designation label pill so it covers no box and no printed text) and, at
//  the response top level, a `label_pill` (the pill size those anchors were
//  computed for). Contract: machineid-bot-integration/API_CONTRACTS.md,
//  "Diagram Overlay API" notes under GET /api/v1/diagram/{page}/hotspots.
//
//  Validation mirrors the served `color` (diagramColorMap.ts): anything
//  malformed is ignored and the hotspot keeps the original "pill just above
//  the box" rule. An absent `label_pill` (SBL500 and every other
//  non-opted-in document) is the same as a malformed one — the original rule,
//  unchanged. An absent `label_anchor` on a page WITH a valid pill is NOT the
//  same as a malformed one since HIDDEN-ONLY-1: absent = no label.
//
//  Pure functions only — no React, no DOM — so they are unit-testable.
// ===================================================================

import {
  DiagramLabelAnchor,
  DiagramLabelAnchorSide,
  DiagramLabelPill,
} from "../../../shared/chatBot/models/chatBotModels";

const LABEL_ANCHOR_SIDES: ReadonlyArray<DiagramLabelAnchorSide> = [
  "above",
  "below",
  "right",
  "left",
  "margin_left",
];

/** Contract: `x_pct` MAY be negative down to -25 (left of the image border). */
export const LABEL_ANCHOR_MIN_X_PCT = -25;
/** Contract: the reserved left gutter is at most 25 % of the image width. */
export const LABEL_GUTTER_MAX_PCT = 25;
/**
 * Defensive cap on each `label_pill` dimension (percent of image width). The
 * contract states no range; today's values are 1.10 / 0.51 / 0.41, so 10 only
 * rejects garbage that would paint a pill across the whole sheet.
 */
const LABEL_PILL_MAX_PCT_W = 10;
/** JetBrains Mono advance width (600 / 1000 em) — the label font. */
const MONO_ADVANCE_EM = 0.6;
/** Text never taller than this share of the pill height. */
const PILL_TEXT_HEIGHT_RATIO = 0.8;

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Validate the top-level `label_pill`. Returns null (= no anchored labels on
 * this page, original rule everywhere) when absent or malformed: without a
 * trustworthy pill size the anchors cannot be drawn the way they were computed.
 */
export const parseLabelPill = (raw: unknown): DiagramLabelPill | null => {
  if (!isObject(raw)) return null;
  const { h_pct_w, char_pct_w, pad_pct_w } = raw;
  if (!isFiniteNumber(h_pct_w) || h_pct_w <= 0 || h_pct_w > LABEL_PILL_MAX_PCT_W) return null;
  if (!isFiniteNumber(char_pct_w) || char_pct_w <= 0 || char_pct_w > LABEL_PILL_MAX_PCT_W) return null;
  if (!isFiniteNumber(pad_pct_w) || pad_pct_w < 0 || pad_pct_w > LABEL_PILL_MAX_PCT_W) return null;
  return { h_pct_w, char_pct_w, pad_pct_w };
};

/**
 * Validate one hotspot's `label_anchor`. Returns null (= original rule for this
 * hotspot) when absent or when any of:
 *   - `x_pct` non-finite or outside [-25, 100]
 *   - `y_pct` non-finite or outside [0, 100]
 *   - `side` not one of above | below | right | left | margin_left
 *   - `leader` present but not a boolean
 * A missing `leader` is read as false (no line). Since OVERLAY-LABEL-PLACEMENT-2
 * (2026-10-02) `leader: true` is valid on EVERY known side — the bot sets it
 * whenever the pill does not touch its box (placed up to 150 px away, left
 * first, before the margin fallback).
 */
export const parseLabelAnchor = (raw: unknown): DiagramLabelAnchor | null => {
  if (!isObject(raw)) return null;
  const { x_pct, y_pct, side, leader } = raw;
  if (!isFiniteNumber(x_pct) || x_pct < LABEL_ANCHOR_MIN_X_PCT || x_pct > 100) return null;
  if (!isFiniteNumber(y_pct) || y_pct < 0 || y_pct > 100) return null;
  if (typeof side !== "string") return null;
  const knownSide = LABEL_ANCHOR_SIDES.find((s) => s === side);
  if (!knownSide) return null;
  if (leader !== undefined && typeof leader !== "boolean") return null;
  return { x_pct, y_pct, side: knownSide, leader: leader === true };
};

/**
 * OVERLAY-LABEL-HIDDEN-ONLY-1 (2026-10-04): which label rule one hotspot gets.
 *   legacy   — the original "pill just above the box" rule
 *   anchored — the pill at the served, validated `anchor`
 *   none     — no label at all
 */
export type HotspotLabelRule = "legacy" | "anchored" | "none";

export interface HotspotLabelPlan {
  rule: HotspotLabelRule;
  /** Non-null iff `rule === "anchored"`. */
  anchor: DiagramLabelAnchor | null;
}

/**
 * Decide the label rule for one hotspot. The bot serves `label_anchor` only
 * for a HIDDEN hotspot (box mostly covered by boxes drawn above it); a box the
 * mouse can already reach needs no label.
 *   - no valid page `label_pill` (SBL500, every non-opted-in document, or a
 *     malformed pill) → legacy, for every hotspot — unchanged behaviour
 *   - valid pill, `label_anchor` absent (undefined or null) → none
 *   - valid pill, `label_anchor` present but fails `parseLabelAnchor` → legacy
 *     (a hidden box must never silently lose its label to bad data)
 *   - valid pill, valid anchor → anchored
 */
export const planHotspotLabel = (
  labelPill: DiagramLabelPill | null | undefined,
  rawAnchor: unknown
): HotspotLabelPlan => {
  if (!labelPill) return { rule: "legacy", anchor: null };
  if (rawAnchor === undefined || rawAnchor === null) return { rule: "none", anchor: null };
  const anchor = parseLabelAnchor(rawAnchor);
  return anchor ? { rule: "anchored", anchor } : { rule: "legacy", anchor: null };
};

/** A rectangle in the overlay's viewBox units (x/w: % of image width, y/h: % of image height). */
export interface LabelRectPct {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Leader line end points, in the same viewBox units. (x1, y1) on the pill, (x2, y2) on the box. */
export interface LabelLeaderLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * OVERLAY-LABEL-PLACEMENT-2: leader line from the pill's edge nearest the box
 * to the box's edge nearest the pill, chosen by the served `side`. Vertical
 * centres for left / margin_left / right, horizontal centres for above / below:
 *   left, margin_left: pill right-edge mid  -> box left-edge mid
 *   right:             pill left-edge mid   -> box right-edge mid
 *   above:             pill bottom-edge mid -> box top-edge mid
 *   below:             pill top-edge mid    -> box bottom-edge mid
 * Both rects must be in the same space (the parent svg's viewBox).
 */
export const computeLabelLeaderLine = (
  side: DiagramLabelAnchorSide,
  pill: LabelRectPct,
  box: LabelRectPct
): LabelLeaderLine => {
  switch (side) {
    case "left":
    case "margin_left":
      return {
        x1: pill.x + pill.w,
        y1: pill.y + pill.h / 2,
        x2: box.x,
        y2: box.y + box.h / 2,
      };
    case "right":
      return {
        x1: pill.x,
        y1: pill.y + pill.h / 2,
        x2: box.x + box.w,
        y2: box.y + box.h / 2,
      };
    case "above":
      return {
        x1: pill.x + pill.w / 2,
        y1: pill.y + pill.h,
        x2: box.x + box.w / 2,
        y2: box.y,
      };
    case "below":
      return {
        x1: pill.x + pill.w / 2,
        y1: pill.y,
        x2: box.x + box.w / 2,
        y2: box.y + box.h,
      };
  }
};

/** Character count as the bot measures it (Python `len` = code points). */
export const labelCharCount = (label: string): number => Array.from(label).length;

/** Pill width in percent of image width: `char_pct_w × len(label) + pad_pct_w`. */
export const computeLabelPillWidthPct = (
  label: string,
  pill: DiagramLabelPill
): number => pill.char_pct_w * labelCharCount(label) + pill.pad_pct_w;

/**
 * Label font size in percent of image width, so a monospace label fills the
 * served character cell (`char_pct_w`) without outgrowing the pill height.
 */
export const computeLabelPillFontSize = (pill: DiagramLabelPill): number =>
  Math.min(pill.char_pct_w / MONO_ADVANCE_EM, pill.h_pct_w * PILL_TEXT_HEIGHT_RATIO);

/**
 * Left gutter to reserve, in percent of image width: the most negative anchor
 * `x_pct` on the page, clamped to 25. 0 when no anchor sits left of the image.
 */
export const computeLabelGutterPct = (
  anchors: ReadonlyArray<DiagramLabelAnchor | null | undefined>
): number => {
  let minX = 0;
  for (const a of anchors) {
    if (a && isFiniteNumber(a.x_pct) && a.x_pct < minX) minX = a.x_pct;
  }
  return minX < 0 ? Math.min(-minX, LABEL_GUTTER_MAX_PCT) : 0;
};

/**
 * object-fit:contain placement of an image that carries a reserved left
 * gutter (`gutterPct` percent of the IMAGE width). The image + gutter block is
 * fitted and centred as a whole; the returned rect is the IMAGE itself (the
 * gutter sits immediately to its left). With gutterPct = 0 this is the plain
 * contain formula.
 */
export const containWithLeftGutter = (
  cw: number,
  ch: number,
  iw: number,
  ih: number,
  gutterPct: number
): { left: number; top: number; width: number; height: number } => {
  const g = Math.max(0, gutterPct) / 100;
  const s = Math.min(cw / (iw * (1 + g)), ch / ih);
  const renderedW = iw * s;
  const renderedH = ih * s;
  const left = (cw - renderedW * (1 + g)) / 2 + renderedW * g;
  const top = (ch - renderedH) / 2;
  return { left, top, width: renderedW, height: renderedH };
};

/** Rendered image width / height; 1 when not measurable yet. */
export const resolveImageAspect = (width: number, height: number): number =>
  width > 0 && height > 0 && Number.isFinite(width / height) ? width / height : 1;
